import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import TimeTrackerAdminHelpModal from './TimeTrackerAdminHelpModal';

const GROUP_LABELS = { CA: '🍁 CA', US: '🇺🇸 US' };
const GROUP_COLORS = { CA: '#c0392b', US: '#1a5276' };

const styles = {
  container: { padding: '1.5rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  title: { fontSize: '1.1rem', fontWeight: 600, color: '#212529', margin: 0 },
  tabs: { display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '2px solid #dee2e6', paddingBottom: '0' },
  tab: (active) => ({
    padding: '6px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem',
    fontWeight: active ? 600 : 400, color: active ? '#007bff' : '#6c757d',
    borderBottom: active ? '2px solid #007bff' : '2px solid transparent', marginBottom: '-2px'
  }),
  addForm: { background: '#f8f9fa', borderRadius: '6px', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #dee2e6' },
  formRow: { display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.8rem', fontWeight: 500, color: '#6c757d' },
  input: { padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', minWidth: '140px' },
  select: { padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { padding: '0.6rem 0.75rem', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', textAlign: 'left', fontWeight: 600, color: '#495057', fontSize: '0.8rem' },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #dee2e6', verticalAlign: 'middle' },
  lockBadge: (locked) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 500,
    background: locked ? '#dc3545' : '#28a745', color: 'white'
  }),
  groupBadge: (group) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
    background: GROUP_COLORS[group] || '#6c757d', color: 'white'
  }),
  btn: (variant) => {
    const map = {
      primary: { background: '#007bff', color: 'white', border: '1px solid #007bff' },
      danger:  { background: '#dc3545', color: 'white', border: '1px solid #dc3545' },
      warning: { background: '#fd7e14', color: 'white', border: '1px solid #fd7e14' },
      success: { background: '#28a745', color: 'white', border: '1px solid #28a745' },
      secondary: { background: 'white', color: '#6c757d', border: '1px solid #6c757d' },
    };
    return { padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, ...map[variant] };
  },
  empty: { textAlign: 'center', padding: '2rem', color: '#6c757d' }
};

export default function PayrollPeriodsAdmin() {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();

  const [showHelp, setShowHelp] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ period_start: '', period_end: '', description: '', payroll_group: 'CA' });
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null); // id of row being locked/unlocked/deleted
  const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'CA' | 'US'

  const fetchPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/actual-spent/payroll-periods');
      if (res.data.success) setPeriods(res.data.data);
    } catch (err) {
      showToast('Failed to load payroll periods', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.period_start || !form.period_end) {
      showToast('Start and end dates are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/actual-spent/payroll-periods', form);
      if (res.data.success) {
        showToast('Payroll period created', 'success');
        setForm({ period_start: '', period_end: '', description: '', payroll_group: form.payroll_group });
        fetchPeriods();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create period';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async (id) => {
    if (!window.confirm('Lock this payroll period? Employees will no longer be able to add or edit entries for these dates.')) return;
    setActionId(id);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/actual-spent/payroll-periods/${id}/lock`);
      if (res.data.success) {
        showToast('Period locked', 'success');
        fetchPeriods();
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to lock period', 'error');
    } finally {
      setActionId(null);
    }
  };

  const handleUnlock = async (id) => {
    if (!window.confirm('Unlock this period? This allows retroactive edits — use only for amendments.')) return;
    setActionId(id);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/actual-spent/payroll-periods/${id}/unlock`);
      if (res.data.success) {
        showToast('Period unlocked', 'success');
        fetchPeriods();
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to unlock period', 'error');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this period? This action cannot be undone.')) return;
    setActionId(id);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'DELETE', `/actual-spent/payroll-periods/${id}`);
      if (res.data.success) {
        showToast('Period deleted', 'success');
        fetchPeriods();
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to delete period', 'error');
    } finally {
      setActionId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = (dateStr.split('T')[0]).split('-');
    return `${m}/${d}/${y}`;
  };

  const visiblePeriods = activeTab === 'ALL' ? periods : periods.filter(p => p.payroll_group === activeTab);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Payroll Periods</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={() => setShowHelp(true)}
          style={{ ...styles.btn('secondary'), background: '#0f766e', color: 'white', border: '1px solid #0f766e' }}
        >
          ❓ Help
        </button>
        <button onClick={fetchPeriods} style={styles.btn('secondary')} disabled={loading}>
          {loading ? 'Refreshing...' : '↺ Refresh'}
        </button>
        </div>
      </div>

      {/* Create Form */}
      <form onSubmit={handleCreate} style={styles.addForm}>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#495057', fontWeight: 500 }}>Create New Period</p>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Group *</label>
            <select style={styles.select} value={form.payroll_group}
              onChange={e => setForm(p => ({ ...p, payroll_group: e.target.value }))}>
              <option value="CA">🍁 CA – Canadian</option>
              <option value="US">🇺🇸 US – American</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Start Date *</label>
            <input type="date" style={styles.input} value={form.period_start}
              onChange={e => setForm(p => ({ ...p, period_start: e.target.value }))} required />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>End Date *</label>
            <input type="date" style={styles.input} value={form.period_end} min={form.period_start}
              onChange={e => setForm(p => ({ ...p, period_end: e.target.value }))} required />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Description (optional)</label>
            <input type="text" style={{ ...styles.input, minWidth: '200px' }} placeholder="e.g. Bi-weekly Mar 1-15"
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <button type="submit" style={styles.btn('primary')} disabled={saving}>
            {saving ? 'Creating...' : '+ Create'}
          </button>
        </div>
      </form>

      {/* Group filter tabs */}
      <div style={styles.tabs}>
        {['ALL', 'CA', 'US'].map(tab => (
          <button key={tab} style={styles.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
            {tab === 'ALL' ? 'All Groups' : tab === 'CA' ? '🍁 Canadian (CA)' : '🇺🇸 American (US)'}
            {tab !== 'ALL' && (
              <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: '#868e96' }}>
                ({periods.filter(p => p.payroll_group === tab).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Periods Table */}
      {loading ? (
        <div style={styles.empty}>Loading...</div>
      ) : visiblePeriods.length === 0 ? (
        <div style={styles.empty}>No payroll periods{activeTab !== 'ALL' ? ` for ${activeTab}` : ''} defined yet.</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Group</th>
              <th style={styles.th}>Period Start</th>
              <th style={styles.th}>Period End</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Locked By</th>
              <th style={styles.th}>Locked At</th>
              <th style={styles.th}>Created By</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visiblePeriods.map(p => (
              <tr key={p.id}>
                <td style={styles.td}>
                  <span style={styles.groupBadge(p.payroll_group)}>{GROUP_LABELS[p.payroll_group] || p.payroll_group}</span>
                </td>
                <td style={styles.td}>{formatDate(p.period_start)}</td>
                <td style={styles.td}>{formatDate(p.period_end)}</td>
                <td style={styles.td}>{p.description || '—'}</td>
                <td style={styles.td}>
                  <span style={styles.lockBadge(!!p.locked_at)}>{p.locked_at ? 'Locked' : 'Open'}</span>
                </td>
                <td style={styles.td}>{p.locked_by_name || '—'}</td>
                <td style={styles.td}>{p.locked_at ? new Date(p.locked_at).toLocaleString() : '—'}</td>
                <td style={styles.td}>{p.created_by_name || '—'}</td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    {p.locked_at ? (
                      <button style={styles.btn('warning')} disabled={actionId === p.id}
                        onClick={() => handleUnlock(p.id)}>Unlock</button>
                    ) : (
                      <>
                        <button style={styles.btn('danger')} disabled={actionId === p.id}
                          onClick={() => handleLock(p.id)}>Lock</button>
                        <button style={styles.btn('secondary')} disabled={actionId === p.id}
                          onClick={() => handleDelete(p.id)}>Delete</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showHelp && <TimeTrackerAdminHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
